<template>
  <div class="container">
    <div class="header">
      <h1>UCCE AI Assistant</h1>
      <p>Ask about Cisco UCCE, CVP, Finesse, CUCM, ECE, call flows, routing, or troubleshooting.</p>
    </div>

    <div class="question-box">
      <textarea
        ref="question"
        v-model="question"
        placeholder="Ask your UCCE question..."
        autofocus
        @keydown.enter.exact.prevent="askQuestion"
      ></textarea>

      <div class="button-row">
        <button :disabled="loading" @click="askQuestion">
          {{ loading ? 'Generating...' : 'Ask UCCE Assistant' }}
        </button>
        <button class="clear-button" @click="clearConversation">Clear</button>
      </div>
    </div>

    <div class="response-container">
      <div class="response-title">UCCE Assistant</div>

      <div v-if="loading && !answer" class="thinking">
        <span>Thinking</span>
        <span class="dot"></span><span class="dot"></span><span class="dot"></span>
      </div>

      <div v-else-if="error" class="error">
        <strong>Error:</strong> {{ error }}
      </div>

      <div v-else-if="answer" class="assistant-response" v-html="renderedAnswer"></div>

      <div v-else-if="finishedEmpty" class="assistant-response"><p>No response was received.</p></div>

      <div v-else class="assistant-response"><p>Your AI response will appear here.</p></div>
    </div>

    <div class="footer">
      <p class="ai-disclaimer">
        AI assistant can make mistakes. Check important info.
      </p>

      <p>
        For help, questions, or assistance, contact:
      </p>

      <p class="contact-name">
        Shaik Abdul Kalaam Majeed
      </p>

      <p>
        <a href="mailto:shaikabdulkalaam.majeed@ttecdigital.com">
          shaikabdulkalaam.majeed@ttecdigital.com
        </a>
      </p>

      <p class="authorship"></p>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({ breaks: true, gfm: true })

export default {
  name: 'UCCEAssistant',
  data() {
    return {
      question: '',
      answer: '',
      error: '',
      loading: false,
      finishedEmpty: false,
      API_URL: ''
    }
  },
  mounted () {
    fetch('/config.json').then(res => res.json()).then(config => {
      this.API_URL = config['API_URL']
    })
  },
  computed: {
    renderedAnswer() {
      return DOMPurify.sanitize(marked.parse(this.answer))
    }
  },
  methods: {
     async ask(question, onText){
        const res = await fetch(`${this.API_URL}/api/ai/ask`, {
            method: 'POST',
            headers: {
                    Authorization: `Bearer ${this.$store.getters.getJwtToken}`,
                    'Content-Type': 'application/json'
                },
            body: JSON.stringify({ question })
        })
 
       /*
        const res = await fetch('/ask', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ question })
        }) */

        if (!res.ok)
           throw new Error('Server returned HTTP ' + res.status)
        
        if (!res.body) throw new Error('Streaming is not supported by this browser.')

        const reader = res.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''

        const handleLine = line => {
            if (!line.trim()) return
            let data
            try { data = JSON.parse(line) } catch (e) { return } // ignore partial/non-JSON lines
            if (data.type === 'text') onText(data.text)
            else if (data.type === 'error') throw new Error(data.message)
        }

        while (true) {
            const { value, done } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''
            for (const line of lines) {
                handleLine(line)
            }
        }
        if (buffer) {
            handleLine(buffer)
        }
    },

    async askQuestion() {
      const q = this.question.trim()

      if (!q) {
        this.$refs.question.focus()
        return
      }

      if (this.loading) return

      this.loading = true
      this.answer = ''
      this.error = ''
      this.finishedEmpty = false

      try {
        await this.ask(q, chunk => {
          this.answer += chunk
        })

        if (!this.answer.trim()) {
          this.answer = ''
          this.finishedEmpty = true
        }
      } catch (e) {
        console.error('UCCE Assistant Error:', e)
        this.error = e.message || 'Failed to get response from assistant.'
      } finally {
        this.loading = false
      }
    },

    clearConversation() {
      this.question = ''
      this.answer = ''
      this.error = ''
      this.finishedEmpty = false
      this.$refs.question.focus()
    }
  }
}
</script>

<style scoped>
.container { width: 92%; max-width: 1100px; margin: 35px auto; }

.header, .question-box, .response-container {
  background: white; border-radius: 14px; box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
}
.header { padding: 25px 30px; margin-bottom: 20px; }
.header h1 { margin: 0 0 8px 0; font-size: 30px; color: #17365d; }
.header p { margin: 0; color: #667085; font-size: 15px; }

.question-box { padding: 20px; margin-bottom: 20px; }
textarea {
  width: 100%; min-height: 90px; resize: vertical; border: 1px solid #d0d5dd;
  border-radius: 10px; padding: 14px; font-size: 16px; font-family: inherit;
  outline: none; transition: border 0.2s;
}
textarea:focus { border-color: #2878c8; box-shadow: 0 0 0 3px rgba(40, 120, 200, 0.1); }

.button-row { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; }

button {
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  background: #17365d;
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

button:hover { background: #0f2948; }
button:active { transform: scale(0.98); }
button:disabled { opacity: 0.6; cursor: not-allowed; }
.clear-button { background: #667085; }
.clear-button:hover { background: #475467; }

.response-container { padding: 25px; min-height: 250px; }
.response-title {
  font-weight: 700; color: #17365d; font-size: 18px; margin-bottom: 15px;
  border-bottom: 1px solid #eaecf0; padding-bottom: 12px;
}

.thinking { display: inline-flex; align-items: center; gap: 8px; color: #667085; font-style: italic; }
.dot { width: 7px; height: 7px; border-radius: 50%; background: #667085; animation: blink 1.2s infinite; }
.dot:nth-child(3) { animation-delay: 0.2s; }
.dot:nth-child(4) { animation-delay: 0.4s; }
@keyframes blink { 0% { opacity: 0.2; } 50% { opacity: 1; } 100% { opacity: 0.2; } }

.error {
  background: #fef3f2; color: #b42318; border: 1px solid #fecdca; border-radius: 8px; padding: 12px;
}
.footer { text-align: center; color: #667085; font-size: 13px; margin-top: 20px; padding-bottom: 20px; }

@media (max-width: 700px) {
  .container { width: 95%; margin: 15px auto; }
  .header h1 { font-size: 24px; }
  .button-row { flex-direction: column; gap: 10px; align-items: stretch; }
  button { width: 100%; }
}
</style>

<!-- Not scoped: styles the HTML injected by v-html (rendered markdown) -->
<style>
.assistant-response { line-height: 1.65; font-size: 15px; color: #1f2937; }
.assistant-response h1 { font-size: 26px; color: #17365d; margin: 25px 0 12px 0; }
.assistant-response h2 { font-size: 21px; color: #17365d; margin: 25px 0 10px 0; border-bottom: 1px solid #eaecf0; padding-bottom: 6px; }
.assistant-response h3 { font-size: 18px; color: #28527a; margin: 20px 0 8px 0; }
.assistant-response p { margin: 9px 0; }
.assistant-response strong { font-weight: 700; }
.assistant-response ul, .assistant-response ol { padding-left: 25px; margin: 10px 0; }
.assistant-response li { margin-bottom: 6px; }
.assistant-response blockquote {
  border-left: 4px solid #17365d; margin: 15px 0; padding: 8px 15px; background: #f7f9fc; color: #475467;
}
.assistant-response table { width: 100%; border-collapse: collapse; margin: 18px 0; font-size: 14px; }
.assistant-response th { background: #17365d; color: white; padding: 10px; border: 1px solid #d0d5dd; text-align: left; }
.assistant-response td { padding: 10px; border: 1px solid #d0d5dd; vertical-align: top; }
.assistant-response tr:nth-child(even) { background: #f8fafc; }
.assistant-response code {
  background: #f2f4f7; border-radius: 5px; padding: 2px 6px;
  font-family: Consolas, Monaco, monospace; font-size: 13px;
}
.assistant-response pre {
  background: #1e1e1e; color: #f8f8f2; border-radius: 8px; padding: 16px; overflow-x: auto; margin: 15px 0;
}
.assistant-response pre code { background: transparent; color: inherit; padding: 0; font-size: 13px; white-space: pre; }
.assistant-response a { color: #1769aa; text-decoration: underline; }

@media (max-width: 700px) {
  .assistant-response table { display: block; overflow-x: auto; }
}
</style>
